import { Navigate, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiArrowRightLine, RiBuilding2Line } from '@remixicon/react'
import { useMemberWorkspaces } from '../../components/layout'
import { EmptyState, PageHeader, SectionSpinner } from '../../components/ui'
import { useAuth } from '../../contexts/AuthContext'

export default function MemberHome() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { workspaces, loading } = useMemberWorkspaces()

  if (loading) return <SectionSpinner />

  // With a single workspace there is nothing to choose: go straight in.
  if (workspaces.length === 1) {
    return <Navigate to={`/w/${workspaces[0].id}`} replace />
  }

  return (
    <>
      <PageHeader
        title={t('member.greeting', { name: user?.name })}
        subtitle={t('member.homeSubtitle')}
      />

      {workspaces.length === 0 ? (
        <div className="rounded-lg bg-white ring-1 ring-gray-200 dark:bg-gray-950 dark:ring-gray-800">
          <EmptyState
            icon={<RiBuilding2Line className="size-6" aria-hidden="true" />}
            title={t('member.noWorkspacesTitle')}
            description={t('member.noWorkspacesDescription')}
          />
        </div>
      ) : (
        <ul className="space-y-3">
          {workspaces.map((workspace) => (
            <li key={workspace.id}>
              <Link
                to={`/w/${workspace.id}`}
                className="group flex items-center justify-between gap-4 rounded-lg bg-white p-5 shadow-sm ring-1 ring-gray-200 transition hover:shadow-md hover:ring-primary-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 dark:bg-gray-950 dark:ring-gray-800 dark:hover:ring-primary-700"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-primary-600 dark:text-gray-50 dark:group-hover:text-primary-400">
                    {workspace.name}
                  </p>
                  <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                    {t(`members.role_${workspace.role}`)}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {workspace.unread_count > 0 && (
                    <span className="flex min-w-5 items-center justify-center rounded-full bg-primary-500 px-1.5 text-xs font-semibold text-white">
                      {workspace.unread_count}
                    </span>
                  )}
                  <RiArrowRightLine
                    className="size-4 text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-primary-500"
                    aria-hidden="true"
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
